import { useCallback, useEffect, useRef, useState } from 'react'

import {
  Box,
  Button,
  Circle,
  CloseButton,
  Dialog,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { Project, getErrorMesssage } from '@infraloop/services'
import { LuPlus, LuSend, LuTrash2 } from 'react-icons/lu'

import useProjects from '../../hooks/useProjects'
import { getInitials, isValidEmail } from '../../utils'

const ViewersModal = ({
  project,
  onClose,
}: {
  /** Project whose viewers to list. */
  project?: Project
  /** Called when the modal requests to close. */
  onClose: () => void
}) => {
  // Hooks
  const { t } = useTranslation()
  const { inviteViewer, isInvitingViewer, removeViewer, isRemovingViewer } = useProjects()

  // States — the invite form starts collapsed; it is revealed by the dashed invite button.
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteError, setInviteError] = useState<string | null>(null)

  // Refs
  const inviteInputRef = useRef<HTMLInputElement>(null)

  // Derived state — shared_user maps user id -> email.
  const viewers = Object.entries(project?.shared_user ?? {}).map(([userId, email]) => ({ userId, email }))

  /**
   * Focus the email field as soon as the invite form is revealed.
   */
  useEffect(() => {
    if (isInviteOpen) inviteInputRef.current?.focus()
  }, [isInviteOpen])

  /**
   * Send the invite, then clear and collapse the form. Failures show inline next to the
   * form (a toast at the top of the page is easy to miss), and the form stays open.
   */
  const handleInvite = useCallback(async () => {
    if (!project) return
    setInviteError(null)
    try {
      await inviteViewer({ projectId: project.project_id, email: inviteEmail.trim() })
      setInviteEmail('')
      setIsInviteOpen(false)
    } catch (error) {
      setInviteError(getErrorMesssage(error, t))
    }
  }, [project, inviteEmail, inviteViewer, t])

  /**
   * Remove an invited viewer from the project.
   * @param userId Viewer to remove.
   */
  const handleRemove = useCallback(
    async (userId: string) => {
      if (!project) return
      try {
        await removeViewer({ projectId: project.project_id, userId })
      } catch {
        // Error feedback is handled by the mutation's onError toast.
      }
    },
    [project, removeViewer],
  )

  return (
    <Dialog.Root open onOpenChange={(details) => !details.open && onClose()} size="lg" placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="16px" maxW="600px">
            <Dialog.Header borderBottom="1px solid" borderColor="gray.100">
              <VStack align="start" gap="7px">
                <HStack gap="7px" fontSize="12px" color="gray.400" fontWeight="medium">
                  <Text as="span">{t('hatsuly.nav.projectList')}</Text>
                  <Text as="span" color="gray.300">
                    /
                  </Text>
                  <Text as="span" color="gray.700" fontWeight="semibold" lineClamp={1}>
                    {project?.project_name}
                  </Text>
                </HStack>
                <Dialog.Title fontSize="19px" fontWeight="bold" color="gray.800">
                  {t('hatsuly.modal.viewers.title')}
                </Dialog.Title>
              </VStack>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>
              <Text
                fontSize="11px"
                fontWeight="bold"
                letterSpacing="0.06em"
                textTransform="uppercase"
                color="gray.500"
                mb="4px"
              >
                {t('hatsuly.modal.viewers.emailColumn')}
              </Text>

              {viewers.map((viewer) => (
                <HStack
                  key={viewer.userId}
                  gap="12px"
                  py="11px"
                  px="6px"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                >
                  <Circle
                    size="32px"
                    bg="primary.50"
                    color="primary.700"
                    fontSize="12px"
                    fontWeight="bold"
                    flexShrink={0}
                  >
                    {getInitials(viewer.email)}
                  </Circle>
                  <Text flex="1" fontSize="14px" color="gray.700" lineClamp={1}>
                    {viewer.email}
                  </Text>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    color="gray.400"
                    _hover={{ bg: 'red.50', color: 'red.600' }}
                    aria-label={t('ui.delete')}
                    disabled={isRemovingViewer}
                    onClick={() => handleRemove(viewer.userId)}
                    data-testid="viewer-delete"
                  >
                    <Icon as={LuTrash2} boxSize="15px" />
                  </IconButton>
                </HStack>
              ))}

              {viewers.length === 0 && (
                <Text py="20px" px="6px" color="gray.400" fontSize="13px">
                  {t('hatsuly.modal.viewers.empty')}
                </Text>
              )}

              {/* Invite affordance — revealed on demand, submits the invite mutation. */}
              {isInviteOpen ? (
                <Box mt="12px" p="14px" bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="11px">
                  <Text as="label" display="block" fontSize="12px" fontWeight="semibold" color="gray.700" mb="7px">
                    {t('hatsuly.modal.viewers.inviteEmailLabel')}
                  </Text>
                  <Input
                    ref={inviteInputRef}
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => {
                      setInviteEmail(e.target.value)
                      setInviteError(null)
                    }}
                    placeholder={t('hatsuly.modal.viewers.emailPlaceholder')}
                    bg="white"
                    borderRadius="8px"
                    data-testid="invite-email"
                  />
                  {inviteError && (
                    <Text fontSize="12px" color="red.600" mt="6px" data-testid="invite-error">
                      {inviteError}
                    </Text>
                  )}
                  <HStack justify="flex-end" gap="9px" mt="12px">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsInviteOpen(false)
                        setInviteError(null)
                      }}
                    >
                      {t('ui.cancel')}
                    </Button>
                    <Button
                      colorPalette="blue"
                      size="sm"
                      disabled={!isValidEmail(inviteEmail)}
                      loading={isInvitingViewer}
                      onClick={handleInvite}
                      data-testid="invite-send"
                    >
                      <Icon as={LuSend} boxSize="15px" />
                      {t('hatsuly.modal.viewers.send')}
                    </Button>
                  </HStack>
                </Box>
              ) : (
                <Button
                  variant="outline"
                  w="100%"
                  mt="12px"
                  py="12px"
                  h="auto"
                  borderStyle="dashed"
                  borderColor="gray.300"
                  borderRadius="9px"
                  color="gray.500"
                  fontSize="13px"
                  fontWeight="semibold"
                  _hover={{ bg: 'primary.50', borderColor: 'primary.300', color: 'primary.700' }}
                  onClick={() => setIsInviteOpen(true)}
                  data-testid="invite-viewer-open"
                >
                  <Icon as={LuPlus} boxSize="16px" />
                  {t('hatsuly.modal.viewers.invite')}
                </Button>
              )}
            </Dialog.Body>

            <Box h="8px" />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default ViewersModal
